import { useSyncExternalStore } from 'react';
import { isSsrRootReleased, subscribeSsrRootRelease } from './ssrShell';

/**
 * Third-party photos wait for the server shell's release (T204-INT).
 *
 * While the SSR document's handoff scaffold is pending, `#root` is invisible
 * and `inert`: nothing the app draws there can be seen yet. A photo from
 * another host that starts downloading in that window only takes bandwidth
 * from what the release is waiting for (measured on Cremona: 5 photos from
 * third-party hosts delayed the data the release depends on by ~1.6 s). So
 * such a photo gets no `src` until the root is released, then loads exactly as
 * before. The box is the same one the `<img>` already reserves, so nothing
 * moves; a document without the scaffold counts as released at once.
 *
 * Not for the event page's first gallery photo: the document preloads it and
 * the preload only helps if the `<img>` asks for the same URL immediately.
 */

/** `false` only while the server scaffold still hides the app root. */
export function useSsrRootReleased(): boolean {
  return useSyncExternalStore(subscribeSsrRootRelease, isSsrRootReleased, () => true);
}

/** Is `url` an absolute address on a host other than the page's own? */
export function isThirdPartyPhoto(url: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * `url` once it may start downloading, `undefined` while it must wait. Same-origin
 * and relative URLs, and any URL once the root is released, pass straight through.
 */
export function useDeferredPhotoUrl(url: string | undefined): string | undefined {
  const released = useSsrRootReleased();
  if (url === undefined) return undefined;
  return released || !isThirdPartyPhoto(url) ? url : undefined;
}
